/**
 * SalesByMarketDashboard
 *
 * Два дашборда на главной:
 *   1) Продажи по дням за выбранный месяц (одна линия — сумма в KZT).
 *   2) Те же дни, разбитые по рынкам (KZ/RU/CIS/TR/Запад).
 *
 * Бэк отдаёт `/api/sales/analytics/daily-by-market` — там же фиксированный
 * mapping валюта → рынок и грубые курсы валют к KZT (см. routes/sales.ts).
 * Возвраты и архив исключаются на бэке.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import client from "../api/client";
import "./SalesByMarketDashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
);

const MONTH_NAMES = [
  "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
  "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек",
];

type Market = "KZ" | "RU" | "AZN" | "BYN" | "GEL" | "KG" | "TR" | "EU" | "West";

const MARKET_LABEL: Record<Market, string> = {
  KZ: "Казахстан (KZT)",
  RU: "Россия (RUB)",
  AZN: "Азербайджан (AZN)",
  BYN: "Беларусь (BYN)",
  GEL: "Грузия (GEL)",
  KG: "Кыргызстан (KGS)",
  TR: "Турция (TRY)",
  EU: "Европа (EUR)",
  West: "Запад (USD)",
};

const MARKET_COLOR: Record<Market, string> = {
  KZ: "#3b82f6",
  RU: "#ef4444",
  AZN: "#22c55e",
  BYN: "#14b8a6",
  GEL: "#84cc16",
  KG: "#ec4899",
  TR: "#f59e0b",
  EU: "#06b6d4",
  West: "#8b5cf6",
};

const MARKETS: Market[] = ["KZ", "RU", "AZN", "BYN", "GEL", "KG", "TR", "EU", "West"];

type PaymentType = "full_payment" | "prepayment" | "postpayment";

const PAYMENT_TYPE_LABEL: Record<PaymentType, string> = {
  full_payment: "Полная оплата",
  prepayment: "Предоплата",
  postpayment: "Постоплата",
};

const PAYMENT_TYPE_COLOR: Record<PaymentType, string> = {
  full_payment: "#0ea5e9",
  prepayment: "#f97316",
  postpayment: "#a855f7",
};

const PAYMENT_TYPES: PaymentType[] = ["full_payment", "prepayment", "postpayment"];

interface ApiResponse {
  days: string[]; // YYYY-MM-DD
  markets: Market[];
  byMarket: Record<Market, number[]>;
  paymentTypes?: PaymentType[];
  byPaymentType?: Record<PaymentType, number[]>;
  byBank?: Record<string, number>;
  total: number[];
}

// Подписи для счетов. Ключи — текущие значения bank-enum в schema.ts.
// Неизвестные значения (старые или новые без перевода) рендерятся as-is.
const BANK_LABEL: Record<string, string> = {
  kaspi: "Kaspi",
  kaspi_gold: "Kaspi Gold",
  kaspi_pay: "Kaspi Pay",
  vtb: "ВТБ",
  tinkoff: "Тинькофф",
  optima: "Optima",
  stripe: "Stripe",
  halyk: "Halyk",
  cash: "Наличные",
  other: "Прочее",
};

const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const formatKzt = (v: number) => Math.round(v).toLocaleString("ru-RU") + "₸";

const SalesByMarketDashboard: React.FC = () => {
  // Месяц для выборки. Дефолт — текущий.
  const [selected, setSelected] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // Кнопки месяцев — последние 12.
  const monthButtons = useMemo(() => {
    const result: { year: number; month: number; label: string }[] = [];
    const now = new Date();
    for (let i = 11; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      result.push({
        year: d.getFullYear(),
        month: d.getMonth(),
        label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      });
    }
    return result;
  }, []);

  // Период: с 1-го числа выбранного месяца по последний день (или сегодня
  // если это текущий месяц — чтобы график не тянулся в будущее пустотой).
  const fromDate = useMemo(
    () => new Date(selected.year, selected.month, 1),
    [selected],
  );
  const toDate = useMemo(() => {
    const now = new Date();
    const isCurrent = selected.year === now.getFullYear() && selected.month === now.getMonth();
    return isCurrent ? now : new Date(selected.year, selected.month + 1, 0);
  }, [selected]);

  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { data: json } = await client.get<ApiResponse>(
          "/sales/analytics/daily-by-market",
          { params: { from: toIsoDate(fromDate), to: toIsoDate(toDate) } },
        );
        if (alive) setData(json);
      } catch (err: any) {
        console.error("[daily-by-market] fetch failed", err);
        if (alive) setError(err?.message || "Ошибка загрузки");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [fromDate, toDate]);

  // Короткие подписи "DD.MM" для оси X.
  const xLabels = useMemo(() => {
    if (!data) return [];
    return data.days.map((iso) => {
      const [, mm, dd] = iso.split("-");
      return `${dd}.${mm}`;
    });
  }, [data]);

  const totalChart = useMemo(() => {
    if (!data) return null;
    const byPt = data.byPaymentType;
    // «Всего» — поденная сумма трёх основных типов (НЕ data.total, который
    // включает экспресс/доставку/доп и был бы выше трёх линий).
    const totalLine = xLabels.map((_, i) =>
      PAYMENT_TYPES.reduce((sum, p) => sum + (byPt?.[p]?.[i] ?? 0), 0),
    );
    return {
      labels: xLabels,
      datasets: [
        ...PAYMENT_TYPES.map((p) => ({
          label: PAYMENT_TYPE_LABEL[p],
          data: byPt?.[p] ?? [],
          borderColor: PAYMENT_TYPE_COLOR[p],
          backgroundColor: PAYMENT_TYPE_COLOR[p] + "30",
          fill: false,
          tension: 0.25,
          pointRadius: 3,
        })),
        {
          label: "Всего",
          data: totalLine,
          borderColor: "#111827",
          backgroundColor: "rgba(17, 24, 39, 0.15)",
          fill: false,
          tension: 0.25,
          pointRadius: 3,
          borderWidth: 2.5,
          borderDash: [6, 4],
        },
      ],
    };
  }, [data, xLabels]);

  // Период таблицы «Баланс по счетам» — свой, независимый от графиков.
  // Кнопки месяцев сбрасывают его на границы выбранного месяца, дальше
  // даты можно выставить руками.
  const [bankRange, setBankRange] = useState(() => ({
    from: toIsoDate(fromDate),
    to: toIsoDate(toDate),
  }));

  useEffect(() => {
    const from = toIsoDate(fromDate);
    const to = toIsoDate(toDate);
    setBankRange((prev) => (prev.from === from && prev.to === to ? prev : { from, to }));
  }, [fromDate, toDate]);

  const bankRangeValid = Boolean(
    bankRange.from && bankRange.to && bankRange.from <= bankRange.to,
  );

  const [byBank, setByBank] = useState<Record<string, number> | null>(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);

  useEffect(() => {
    if (!bankRangeValid) return;
    let alive = true;
    setBankLoading(true);
    setBankError(null);

    (async () => {
      try {
        const { data: json } = await client.get<ApiResponse>(
          "/sales/analytics/daily-by-market",
          { params: { from: bankRange.from, to: bankRange.to } },
        );
        if (alive) setByBank(json.byBank ?? {});
      } catch (err: any) {
        console.error("[daily-by-market] banks fetch failed", err);
        if (alive) setBankError(err?.message || "Ошибка загрузки");
      } finally {
        if (alive) setBankLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [bankRange.from, bankRange.to, bankRangeValid]);

  // Балансы по счетам — отсортированы по сумме, desc.
  const bankRows = useMemo(() => {
    if (!byBank) return [];
    return Object.entries(byBank)
      .map(([bank, kzt]) => ({ bank, kzt }))
      .sort((a, b) => b.kzt - a.kzt);
  }, [byBank]);
  const bankTotal = useMemo(
    () => bankRows.reduce((s, r) => s + r.kzt, 0),
    [bankRows],
  );

  const marketChart = useMemo(() => {
    if (!data) return null;
    return {
      labels: xLabels,
      datasets: MARKETS.map((m) => ({
        label: MARKET_LABEL[m],
        data: data.byMarket[m] ?? [],
        borderColor: MARKET_COLOR[m],
        backgroundColor: MARKET_COLOR[m] + "30",
        fill: false,
        tension: 0.25,
        pointRadius: 3,
      })),
    };
  }, [data, xLabels]);

  const sumTotal = data?.total.reduce((a, b) => a + b, 0) ?? 0;

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { position: "bottom" as const, labels: { boxWidth: 14, font: { size: 11 } } },
        tooltip: {
          callbacks: {
            label: (ctx: any) => `${ctx.dataset.label}: ${formatKzt(ctx.parsed.y)}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (v: any) => `${Math.round(Number(v) / 1000)}k`,
            font: { size: 11 },
          },
        },
        x: { ticks: { font: { size: 11 } } },
      },
    }),
    [],
  );

  return (
    <div className="sbm">
      <div className="sbm__header">
        <h2 className="sbm__title">Продажи по дням за месяц</h2>
        <div className="sbm__months">
          {monthButtons.map((mb) => {
            const active = mb.year === selected.year && mb.month === selected.month;
            return (
              <button
                key={`${mb.year}-${mb.month}`}
                className={"sbm__month-btn" + (active ? " sbm__month-btn--active" : "")}
                onClick={() => setSelected({ year: mb.year, month: mb.month })}
                type="button"
              >
                {mb.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="sbm__error">{error}</div>}
      {loading && <div className="sbm__hint">Загрузка…</div>}

      {data && (
        <>
          <div className="sbm__card">
            <div className="sbm__card-head">
              <span>По типу оплаты (в KZT)</span>
              <strong>{formatKzt(sumTotal)}</strong>
            </div>
            <div className="sbm__chart-wrap">
              {totalChart && <Line data={totalChart} options={chartOptions} />}
            </div>
          </div>

          <div className="sbm__card">
            <div className="sbm__card-head">
              <span>По рынкам (в KZT, по фикс. курсу)</span>
            </div>
            <div className="sbm__chart-wrap">
              {marketChart && <Line data={marketChart} options={chartOptions} />}
            </div>
          </div>
        </>
      )}

      {/* Баланс по счетам — со своим периодом, поэтому вне блока графиков. */}
      <div className="sbm__card">
        <div className="sbm__card-head">
          <span>Баланс по счетам (в KZT)</span>
          <strong>{formatKzt(bankTotal)}</strong>
        </div>

        <div className="sbm__range">
          <label className="sbm__range-field">
            <span>С</span>
            <input
              type="date"
              value={bankRange.from}
              onChange={(e) => setBankRange((p) => ({ ...p, from: e.target.value }))}
            />
          </label>
          <label className="sbm__range-field">
            <span>По</span>
            <input
              type="date"
              value={bankRange.to}
              onChange={(e) => setBankRange((p) => ({ ...p, to: e.target.value }))}
            />
          </label>
          <button
            type="button"
            className="sbm__range-reset"
            onClick={() =>
              setBankRange({ from: toIsoDate(fromDate), to: toIsoDate(toDate) })
            }
          >
            Выбранный месяц
          </button>
        </div>

        {!bankRangeValid ? (
          <div className="sbm__hint">Укажите период: дата «с» не позже даты «по».</div>
        ) : bankError ? (
          <div className="sbm__error">{bankError}</div>
        ) : bankLoading && !byBank ? (
          <div className="sbm__hint">Загрузка…</div>
        ) : bankRows.length === 0 ? (
          <div className="sbm__hint">Нет продаж за период.</div>
        ) : (
          <table className="sbm__bank-table">
            <tbody>
              {bankRows.map((r) => (
                <tr key={r.bank}>
                  <td className="sbm__bank-name">{BANK_LABEL[r.bank] ?? r.bank}</td>
                  <td className="sbm__bank-amount">{formatKzt(r.kzt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SalesByMarketDashboard;
